import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Alert, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Pagination, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Tabs, Tooltip, Typography, Chip, Paper } from '@mui/material';
import { AccountBalance, Add, Delete, Edit, Payments, PostAdd, Receipt, ShoppingCart } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import TextField from '../components/common/TextField';
import ExpenseDialog from '../components/expenses/ExpenseDialog';
import PayExpenseDialog from '../components/expenses/PayExpenseDialog';
import type { OneTimeExpenseRow } from '../types';
import { formatDay, formatMoney } from '../components/commercial/contact';

interface SupplierForm {
  name: string;
  cuit?: string;
  company?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  paymentTermDays?: number;
  isActive: boolean;
  types: string[];
}

export const SuppliersPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<SupplierForm>({ name: '', isActive: true, types: [] });

  // Account dialog state
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountSupplier, setAccountSupplier] = useState<any | null>(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountSummary, setAccountSummary] = useState<any | null>(null);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [accountTab, setAccountTab] = useState(0);
  const [accountError, setAccountError] = useState<string | null>(null);

  // Deudas sin orden de compra (gastos "a pagar")
  const [debtSupplier, setDebtSupplier] = useState<{ id: number; name: string } | null | undefined>(undefined);
  const [expenseCategories, setExpenseCategories] = useState<string[]>([]);
  const [expenseDebts, setExpenseDebts] = useState<OneTimeExpenseRow[]>([]);
  const [payingDebt, setPayingDebt] = useState<OneTimeExpenseRow | null>(null);
  useEffect(() => { api.getExpenseCategories().then(setExpenseCategories).catch(() => setExpenseCategories(['General'])); }, []);
  const loadExpenseDebts = async (supplierId: number) => {
    try { setExpenseDebts((await api.getOneTimeExpenses({ pendingOnly: true })).filter(e => e.supplierId === supplierId)); }
    catch { setExpenseDebts([]); }
  };

  // Derived data for account dialog
  const pendingPurchases = useMemo(() => {
    return purchases.filter((p: any) => {
      const total = Number(p.total || 0);
      const paid = Number(p.paidAmount || 0);
      return paid < total;
    });
  }, [purchases]);

  const monthlyPurchaseData = useMemo(() => {
    if (!purchases.length) return [];
    const monthMap: Record<string, { pagado: number; pendiente: number }> = {};
    purchases.forEach((p: any) => {
      const date = p.purchaseDate ? new Date(p.purchaseDate) : null;
      if (!date) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { pagado: 0, pendiente: 0 };
      const total = Number(p.total || 0);
      const paid = Number(p.paidAmount || 0);
      monthMap[key].pagado += paid;
      monthMap[key].pendiente += (total - paid);
    });
    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, data]) => ({ month, ...data }));
  }, [purchases]);

  const load = async () => {
    const res = await api.getSuppliers({ page, pageSize: 10, search });
    setSuppliers(res.suppliers);
    setTotalPages(res.totalPages || 1);
  };

  const debouncedSearch = useDebounce(search, 300);
  const isFirstRender = useRef(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const onSearch = async () => {
    setPage(1);
    await load();
  };

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setForm({ name: '', isActive: true, types: [] });
    setOpen(true);
  };

  const openEdit = (s: any) => {
    setEditing(s);
    setSaveError(null);
    setForm({
      name: s.name,
      cuit: s.cuit || '',
      company: s.company || '',
      email: s.email || '',
      phone: s.phone || '',
      address: s.address || '',
      city: s.city || '',
      state: s.state || '',
      postalCode: s.postalCode || '',
      country: s.country || '',
      paymentTermDays: s.paymentTermDays || 0,
      isActive: s.isActive,
      types: s.types || [],
    });
    setOpen(true);
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    if (!form.name) return;
    setSaveError(null);
    try {
      if (editing) {
        await api.updateSupplier(editing.id, form);
      } else {
        await api.createSupplier(form);
      }
      setOpen(false);
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Error al guardar el proveedor';
      setSaveError(msg);
      console.error('Error saving supplier:', e);
    }
  };

  const remove = async (id: number) => {
    await api.deleteSupplier(id);
    await load();
  };

  const openAccount = async (supplier: any) => {
    setAccountSupplier(supplier);
    setAccountOpen(true);
    setAccountLoading(true);
    setAccountError(null);
    setAccountTab(0);
    try {
      const [summary, purchasesRes] = await Promise.all([
        api.getSupplierAccountSummary(supplier.id),
        api.getSupplierPendingPurchases(supplier.id),
      ]);
      setAccountSummary(summary);
      setPurchases(purchasesRes || []);
      await loadExpenseDebts(supplier.id);
    } catch (e: any) {
      setAccountError(e?.response?.data?.message || 'No se pudo cargar la cuenta');
    } finally {
      setAccountLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Proveedores</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PostAdd />} onClick={() => setDebtSupplier(null)}>Cargar deuda a proveedor</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Nuevo proveedor</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Buscar por nombre, empresa o CUIT" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
        <Button variant="outlined" onClick={onSearch}>Buscar</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>CUIT</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell>Categorías</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.cuit || '—'}</TableCell>
                <TableCell>{s.company}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{s.phone}</TableCell>
                <TableCell>{s.city}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(s.types || []).map((t: string) => (
                      <Chip key={t} size="small" label={t} />
                    ))}
                    {(s.types || []).length === 0 && <Typography variant="caption" color="text.secondary">Sin categorías</Typography>}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Cuenta"><IconButton size="small" onClick={() => openAccount(s)} color="primary"><AccountBalance /></IconButton></Tooltip>
                  <Tooltip title="Cargar deuda (factura sin orden de compra)"><IconButton size="small" onClick={() => setDebtSupplier({ id: s.id, name: s.name })}><PostAdd /></IconButton></Tooltip>
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(s)}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" onClick={() => remove(s.id)}><Delete /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth />
              <TextField label="CUIT" value={form.cuit || ''} onChange={e => setForm({ ...form, cuit: e.target.value })} fullWidth placeholder="XX-XXXXXXXX-X" />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {['Insumos', 'MateriaPrima', 'Servicios'].map((t) => (
                <Chip
                  key={t}
                  label={t}
                  color={form.types.includes(t) ? 'primary' : 'default'}
                  onClick={() => {
                    const exists = form.types.includes(t);
                    setForm({ ...form, types: exists ? form.types.filter(x => x !== t) : [...form.types, t] });
                  }}
                  clickable
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Empresa" value={form.company || ''} onChange={e => setForm({ ...form, company: e.target.value })} fullWidth />
              <TextField label="Email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Teléfono" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} fullWidth />
              <TextField label="Ciudad" value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Dirección" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Provincia/Estado" value={form.state || ''} onChange={e => setForm({ ...form, state: e.target.value })} fullWidth />
              <TextField label="CP" value={form.postalCode || ''} onChange={e => setForm({ ...form, postalCode: e.target.value })} fullWidth />
              <TextField label="País" value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="number" label="Plazo pago (días)" value={form.paymentTermDays ?? 0} onChange={e => setForm({ ...form, paymentTermDays: Number(e.target.value) })} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Guardar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Account Dialog */}
      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Cuenta Proveedor - {accountSupplier?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {accountLoading && <LinearProgress sx={{ mb: 2 }} />}
          {accountError && <Alert severity="error" sx={{ mb: 2 }}>{accountError}</Alert>}
          {accountSummary && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Balance</Typography>
                      <Typography variant="h5" color={Number(accountSummary.currentBalance || 0) > 0 ? 'error.main' : 'success.main'}>
                        ${Number(accountSummary.currentBalance || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Compras Pendientes</Typography>
                      <Typography variant="h5" color={Number(accountSummary.pendingPurchasesCount || 0) > 0 ? 'warning.main' : 'text.primary'}>
                        {accountSummary.pendingPurchasesCount || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Total Comprado</Typography>
                      <Typography variant="h5">${Number(accountSummary.totalPurchased || accountSummary.pendingAmount || 0).toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Monto Pendiente</Typography>
                      <Typography variant="h5" color={Number(accountSummary.pendingAmount || 0) > 0 ? 'error.main' : 'text.primary'}>
                        ${Number(accountSummary.pendingAmount || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Monthly Purchase History Chart */}
              {purchases.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Historial de Compras Mensual</Typography>
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyPurchaseData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
                        <RechartsTooltip formatter={(v: any) => `$ ${Math.round(Number(v) || 0).toLocaleString('es-AR')}`} />
                        <Legend />
                        <Bar dataKey="pagado" name="Pagado" fill="#16a34a" stackId="a" />
                        <Bar dataKey="pendiente" name="Pendiente de Pago" fill="#f59e0b" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              )}

              <Tabs value={accountTab} onChange={(_, v) => setAccountTab(v)} sx={{ mb: 2 }}>
                <Tab label="Compras" icon={<ShoppingCart />} iconPosition="start" />
                <Tab label="Pendientes de Pago" icon={<Receipt />} iconPosition="start" />
              </Tabs>

              {/* Tab 0: All Purchases */}
              {accountTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Referencia</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Monto Pagado</TableCell>
                        <TableCell align="right">Saldo Pendiente</TableCell>
                        <TableCell>Estado Pago</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchases.map((p: any) => {
                        const total = Number(p.total || 0);
                        const paid = Number(p.paidAmount || 0);
                        const remaining = total - paid;
                        const status = paid >= total ? 'Pagado' : paid > 0 ? 'Pago Parcial' : 'Pendiente';
                        return (
                          <TableRow key={p.batchId} hover>
                            <TableCell>{p.reference}</TableCell>
                            <TableCell>{p.purchaseDate ? new Date(p.purchaseDate).toLocaleDateString() : '\u2014'}</TableCell>
                            <TableCell align="right">${total.toLocaleString()}</TableCell>
                            <TableCell align="right">${paid.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: remaining > 0 ? 'bold' : 'normal', color: remaining > 0 ? 'error.main' : 'inherit' }}>
                              ${remaining.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={p.paymentStatus || status}
                                color={status === 'Pagado' ? 'success' : status === 'Pago Parcial' ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {purchases.length === 0 && (
                        <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Sin compras registradas</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                    {purchases.length > 0 && (
                      <TableFooter>
                        <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.875rem', color: 'text.primary', borderTop: '2px solid', borderColor: 'divider' } }}>
                          <TableCell colSpan={2}>Totales</TableCell>
                          <TableCell align="right">${purchases.reduce((s: number, p: any) => s + Number(p.total || 0), 0).toLocaleString()}</TableCell>
                          <TableCell align="right">${purchases.reduce((s: number, p: any) => s + Number(p.paidAmount || 0), 0).toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main !important' }}>
                            ${purchases.reduce((s: number, p: any) => s + (Number(p.total || 0) - Number(p.paidAmount || 0)), 0).toLocaleString()}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}

              {/* Tab 1: Pending Payments Only */}
              {accountTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Referencia</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Monto Pagado</TableCell>
                        <TableCell align="right">Saldo Pendiente</TableCell>
                        <TableCell>Estado Pago</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingPurchases.map((p: any) => {
                        const total = Number(p.total || 0);
                        const paid = Number(p.paidAmount || 0);
                        const remaining = total - paid;
                        const status = paid > 0 ? 'Pago Parcial' : 'Pendiente';
                        return (
                          <TableRow key={p.batchId} hover>
                            <TableCell>{p.reference}</TableCell>
                            <TableCell>{p.purchaseDate ? new Date(p.purchaseDate).toLocaleDateString() : '\u2014'}</TableCell>
                            <TableCell align="right">${total.toLocaleString()}</TableCell>
                            <TableCell align="right">${paid.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                              ${remaining.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={p.paymentStatus || status}
                                color={status === 'Pago Parcial' ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {pendingPurchases.length === 0 && (
                        <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Sin compras pendientes de pago</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                    {pendingPurchases.length > 0 && (
                      <TableFooter>
                        <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.875rem', color: 'text.primary', borderTop: '2px solid', borderColor: 'divider' } }}>
                          <TableCell colSpan={2}>Totales</TableCell>
                          <TableCell align="right">${pendingPurchases.reduce((s: number, p: any) => s + Number(p.total || 0), 0).toLocaleString()}</TableCell>
                          <TableCell align="right">${pendingPurchases.reduce((s: number, p: any) => s + Number(p.paidAmount || 0), 0).toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main !important' }}>
                            ${pendingPurchases.reduce((s: number, p: any) => s + (Number(p.total || 0) - Number(p.paidAmount || 0)), 0).toLocaleString()}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}

              {accountTab === 1 && (
                <Box sx={{ mt: 3 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Deudas sin orden de compra</Typography>
                    <Button size="small" startIcon={<PostAdd />} onClick={() => accountSupplier && setDebtSupplier({ id: accountSupplier.id, name: accountSupplier.name })}>Cargar deuda</Button>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Vence</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenseDebts.map(d => (
                        <TableRow key={d.id}>
                          <TableCell>{d.description}</TableCell>
                          <TableCell>{formatDay(d.dueDate)}</TableCell>
                          <TableCell align="right">{formatMoney(d.amount)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{formatMoney(d.balance)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Registrar pago"><IconButton size="small" color="primary" onClick={() => setPayingDebt(d)}><Payments fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {expenseDebts.length === 0 && (
                        <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Sin deudas cargadas como gasto</Typography></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ExpenseDialog
        open={debtSupplier !== undefined}
        defaultPayable
        defaultSupplier={debtSupplier}
        categories={expenseCategories}
        onClose={() => setDebtSupplier(undefined)}
        onSaved={e => { if (accountOpen && accountSupplier && e.supplierId === accountSupplier.id) loadExpenseDebts(accountSupplier.id); }}
      />
      <PayExpenseDialog expense={payingDebt} onClose={() => setPayingDebt(null)}
        onPaid={() => accountSupplier && loadExpenseDebts(accountSupplier.id)} />
    </Box>
  );
};
